import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Sparkles, 
  Trophy, 
  ShieldCheck, 
  Zap, 
  Layers, 
  Users, 
  Award, 
  FileCode, 
  CheckCircle2, 
  ArrowRight,
  Palette,
  Eye,
  Lock,
  Compass,
  Star
} from 'lucide-react';
import Button from '../components/Button';
import './About.css';

export default function About() {
  const formatList = [
    { ext: '.FIG', name: 'Figma Systems', desc: 'Auto-layout components, color tokens, and production-ready screen flows.' },
    { ext: '.PSD', name: 'Adobe Photoshop', desc: 'Layered smart objects, high-end packaging, and photo-real branding mockups.' },
    { ext: '.AI', name: 'Adobe Illustrator', desc: 'Infinitely scalable vector brand marks, glyphs, and iconography.' },
    { ext: '.AEP', name: 'After Effects Comps', desc: 'Kinetic typography, motion ident loops, and 60fps UI interaction reels.' },
    { ext: '.BLEND', name: 'Blender 3D Scenes', desc: 'Procedural lighting rigs, luxury material shaders, and 3D assets.' },
    { ext: '.MP4', name: '4K Motion Previews', desc: 'High-bitrate interaction demos and responsive prototype recordings.' },
  ];

  const pillars = [
    {
      icon: Zap,
      title: 'Curated Design Challenges',
      desc: 'Rapid 24-hour Blitz Sprints, Weekly Creative Jams, and seasonal Championship Bounties designed to push creative boundaries.',
    },
    {
      icon: ShieldCheck,
      title: 'Dual-Stage Jury & Community Evaluation',
      desc: 'Entries are appraised through transparent community upvoting coupled with expert Design Director jury rubrics across visual hierarchy, typography, and original craft.',
    },
    {
      icon: Users,
      title: 'Creator Guild & Critique Exchange',
      desc: 'A collaborative space for designers to post work-in-progress proofs, request feedback, explore type scales, and discover peers.',
    },
    {
      icon: Award,
      title: 'Merit-Based Creator Trust',
      desc: 'Designers earn verified creator status and portfolio credibility through original template downloads, contest wins, and client satisfaction.',
    },
  ];

  return (
    <div className="about-page page-container" style={{ paddingBottom: '100px', paddingTop: '32px' }}>
      {/* ── Hero Section ── */}
      <section className="about-hero glass-panel">
        <div className="about-hero-badge">
          <Sparkles size={14} className="text-primary" />
          <span>THE BUILDIVE CREATIVE ECOSYSTEM</span>
        </div>

        <h1 className="about-hero-title">
          Where Visionary Designers <span className="gradient-text">Create, Compete, and Lead.</span>
        </h1>

        <p className="about-hero-subtitle">
          Buildive is the dedicated creative platform for UI/UX architects, 3D artists, and brand designers. We unite a premium digital asset marketplace with creative challenge arenas, verified creator portfolios, and real-time collaboration.
        </p>

        <div className="about-hero-cta">
          <Link to="/competitions">
            <Button variant="primary" size="lg">
              Explore Active Challenges <ArrowRight size={18} />
            </Button>
          </Link>
          <Link to="/browse">
            <Button variant="secondary" size="lg">
              Browse Marketplace
            </Button>
          </Link>
        </div>
      </section>

      {/* ── Core Pillars ── */}
      <section className="about-pillars-section">
        <div className="section-header" style={{ marginBottom: '28px' }}>
          <div>
            <span className="section-eyebrow">CORE ARCHITECTURE</span>
            <h2 className="text-h2">Engineered for Creative Excellence</h2>
          </div>
        </div>

        <div className="pillars-grid">
          {pillars.map((pillar, idx) => {
            const Icon = pillar.icon;
            return (
              <div key={idx} className="pillar-card glass-panel interactive-card">
                <div className="pillar-icon-wrap">
                  <Icon size={24} color="var(--primary)" />
                </div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 800, marginBottom: '8px' }}>{pillar.title}</h3>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{pillar.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Supported File Formats ── */}
      <section className="about-formats-section glass-panel" style={{ borderRadius: '28px', padding: '40px', marginTop: '48px' }}>
        <div className="section-header" style={{ marginBottom: '28px' }}>
          <div>
            <span className="section-eyebrow">SUPPORTED CREATIVE TOOLS</span>
            <h2 className="text-h2">Built for Modern Professional Workflows</h2>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
          {formatList.map((item, idx) => (
            <div key={idx} style={{ background: 'var(--bg-base)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'inline-block', padding: '4px 10px', background: 'var(--primary-subtle)', color: 'var(--primary-text)', borderRadius: '6px', fontWeight: 800, fontSize: '0.75rem', marginBottom: '10px' }}>
                {item.ext}
              </div>
              <h4 style={{ fontSize: '1rem', fontWeight: 800, marginBottom: '6px' }}>{item.name}</h4>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Evaluation Rubric Standards ── */}
      <section style={{ marginTop: '48px' }}>
        <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '28px', padding: '40px' }}>
          <span className="section-eyebrow">CURATION & RUBRIC</span>
          <h2 className="text-h2" style={{ marginBottom: '12px' }}>How Creative Work is Evaluated</h2>
          <p style={{ fontSize: '0.9375rem', color: 'var(--text-secondary)', maxWidth: '700px', lineHeight: 1.6, marginBottom: '28px' }}>
            To ensure selections represent peak design craft rather than social popularity, our jury and curation pipeline focuses on four fundamental vectors:
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
            <div style={{ background: 'var(--bg-base)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border-subtle)' }}>
              <CheckCircle2 size={20} color="var(--accent)" style={{ marginBottom: '8px' }} />
              <strong style={{ fontSize: '0.9375rem', display: 'block', marginBottom: '4px' }}>Visual Hierarchy & Flow</strong>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>Intentional eye guidance, balance, and structured focal points.</p>
            </div>

            <div style={{ background: 'var(--bg-base)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border-subtle)' }}>
              <CheckCircle2 size={20} color="var(--accent)" style={{ marginBottom: '8px' }} />
              <strong style={{ fontSize: '0.9375rem', display: 'block', marginBottom: '4px' }}>Typographic Discipline</strong>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>Harmonious font pairings, strict type scales, and contrast ratios.</p>
            </div>

            <div style={{ background: 'var(--bg-base)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border-subtle)' }}>
              <CheckCircle2 size={20} color="var(--accent)" style={{ marginBottom: '8px' }} />
              <strong style={{ fontSize: '0.9375rem', display: 'block', marginBottom: '4px' }}>Color Theory & Atmosphere</strong>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>Cohesive palettes, brand voice alignment, and purposeful accent tones.</p>
            </div>

            <div style={{ background: 'var(--bg-base)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border-subtle)' }}>
              <CheckCircle2 size={20} color="var(--accent)" style={{ marginBottom: '8px' }} />
              <strong style={{ fontSize: '0.9375rem', display: 'block', marginBottom: '4px' }}>Source File Hygiene</strong>
              <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}>Organized layers, naming standards, and scalable component architecture.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
